import { STORAGE_KEYS } from '../constants';

/**
 * Typed access to localStorage.
 *
 * `JSON.parse(localStorage.getItem(key))` was spelled out at every call site.
 * Two problems with that: the key was a bare string each time, and `JSON.parse`
 * throws on a malformed value — one corrupt entry (a half-written token, a
 * value set by an older build) took down the component that read it, which for
 * the cached user is the whole shell.
 *
 * Reads never throw; a bad value is treated as absent.
 */

export const readJSON = (key, fallback = null) => {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null || raw === 'undefined') return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    // Corrupt entry — drop it rather than throwing on every later read.
    try {
      localStorage.removeItem(key);
    } catch {
      /* storage unavailable (private mode, quota) — nothing to clean up */
    }
    return fallback;
  }
};

export const writeJSON = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    // Quota exceeded or storage blocked. The caller's state is still correct in
    // memory, so this is not worth failing the interaction over.
    return false;
  }
};

export const remove = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {
    /* storage unavailable */
  }
};

/** The signed-in user cached at login. Shape: `{ data: user }`. */
export const getCachedUser = () => readJSON(STORAGE_KEYS.USER)?.data ?? null;

export const getAuthToken = () => readJSON(STORAGE_KEYS.AUTH_TOKEN);

/**
 * Light is the product default, so an absent preference reads as light. Keep the
 * fallback in step with the boot script in public/index.html, which paints the
 * theme class before React mounts.
 */
export const isDarkModeStored = () => readJSON(STORAGE_KEYS.DARK_MODE, false) === true;

/**
 * The app-lock latch, persisted so a locked app is still locked after a reload.
 *
 * Stored as a bare `'true'`/`'false'` string rather than JSON, which is the shape
 * already on disk in every installed browser — changing the encoding here would
 * silently unlock every currently-locked session on deploy.
 *
 * These exist because the reads and writes were spread across `AppReducer` as raw
 * `localStorage` calls INSIDE the reducer. A reducer has to be a pure function of
 * state and action: a synchronous storage write on the dispatch path blocks the
 * main thread, cannot be replayed or unit-tested, and under React 18 concurrent
 * rendering may run more than once per action. The writes now happen in the saga
 * that owns the transition; only this module touches the key.
 */
export const isAppLockedStored = () => {
  try {
    return localStorage.getItem(STORAGE_KEYS.IS_APP_LOCKED) === 'true';
  } catch {
    // Storage blocked (private mode, embedded webview). Default to unlocked —
    // the passcode gate is re-evaluated from the server profile on load anyway.
    return false;
  }
};

export const setAppLocked = (locked) => {
  try {
    localStorage.setItem(STORAGE_KEYS.IS_APP_LOCKED, locked ? 'true' : 'false');
  } catch {
    /* storage unavailable — in-memory state is still correct */
  }
};

export const clearAppLocked = () => remove(STORAGE_KEYS.IS_APP_LOCKED);

const Storage = {
  readJSON,
  writeJSON,
  remove,
  getCachedUser,
  getAuthToken,
  isDarkModeStored,
  isAppLockedStored,
  setAppLocked,
  clearAppLocked,
};

export default Storage;
